import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { getCategoryByKey } from "../api/api";
import { fetchArticleById, parseTags, toggleStatus, toggleTrending } from "../api/articlesApi";
import Loader from "./Loader";
import ToggleSwitch from "./ToggleSwitch";
import "../styles/PostDetail.css";

export default function PostDetail() {
  const { categoryKey, id } = useParams();
  const category = getCategoryByKey(categoryKey);
  const location = useLocation();
  const passedPost = location.state?.post;

  const [post, setPost] = useState(passedPost || null);
  const [loading, setLoading] = useState(!passedPost);
  const [error, setError] = useState("");
  const [toggling, setToggling] = useState(false);
  const [toggleError, setToggleError] = useState("");

  useEffect(() => {
    // Already have the post from the list page's route state — no need to refetch.
    if (passedPost || !category) return;
    let cancelled = false;

    setLoading(true);
    setError("");
    fetchArticleById(category.endpoint, id)
      .then((data) => {
        if (!cancelled) setPost(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err?.response?.data?.message || err.message || "Failed to load post."
          );
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [category, id, passedPost]);

  if (!category) return <p>Unknown category.</p>;
  if (loading) return <Loader label="Loading post..." />;
  if (error) return <p className="postdetail-error">{error}</p>;
  if (!post) return <p>Post not found.</p>;

  const tags = parseTags(post.tags);
  const published = Boolean(Number(post.status));
  const trending = Boolean(Number(post.trending));

  async function handleToggleStatus() {
    const next = !published;
    setToggleError("");
    setToggling(true);
    try {
      await toggleStatus(post.id, category.label, next);
      setPost((prev) => ({ ...prev, status: next ? 1 : 0 }));
    } catch (err) {
      setToggleError(
        err?.response?.data?.message || err.message || "Could not update status."
      );
    } finally {
      setToggling(false);
    }
  }

  async function handleToggleTrending() {
    const next = !trending;
    setToggleError("");
    setToggling(true);
    try {
      await toggleTrending(post.id, category.label, next);
      setPost((prev) => ({ ...prev, trending: next ? 1 : 0 }));
    } catch (err) {
      setToggleError(
        err?.response?.data?.message ||
          err.message ||
          "Could not update trending status."
      );
    } finally {
      setToggling(false);
    }
  }

  return (
    <div className="postdetail">
      <Link className="postdetail-back" to={`/category/${categoryKey}`}>
        ← Back to {category.label}
      </Link>

      {post.imageUrl && (
        <div className="postdetail-image">
          <img src={post.imageUrl} alt={post.title} />
          {trending && <span className="postdetail-trend-flag">Trending</span>}
        </div>
      )}

      <div className="postdetail-toggles">
        {toggleError && <p className="postdetail-toggle-error">{toggleError}</p>}
        <ToggleSwitch
          label={published ? "Published" : "Unpublished"}
          checked={published}
          onChange={handleToggleStatus}
          disabled={toggling}
        />
        <ToggleSwitch
          label={trending ? "Trending" : "Not trending"}
          checked={trending}
          onChange={handleToggleTrending}
          disabled={toggling}
          tone="trend"
        />
      </div>

      {post.createdAt && (
        <p className="postdetail-date">
          {new Date(post.createdAt).toLocaleString()}
        </p>
      )}

      <h1 className="postdetail-title">{post.title}</h1>
      {post.summary && <p className="postdetail-summary">{post.summary}</p>}

      {tags.length > 0 && (
        <div className="postdetail-tags">
          {tags.map((t, i) => (
            <span className="postdetail-tag" key={i}>
              {t}
            </span>
          ))}
        </div>
      )}

      <div className="postdetail-body">
        {(post.description || "").split(/\n+/).map((para, i) =>
          para.trim() ? <p key={i}>{para}</p> : null
        )}
      </div>

      <div className="postdetail-actions">
        <Link className="postdetail-edit-btn" to={`/category/${categoryKey}/edit/${post.id}`}>
          Edit this post
        </Link>
      </div>
    </div>
  );
}
